import { CoursePageFooter } from '@/components/course/CoursePageFooter'
import { CoursePagePremiumLoader } from '@/components/course/CoursePagePremiumLoader'
import { HelpScoutBeacon } from '@/components/HelpScoutBeacon'
import { Container, ErrorDisplay } from '@/components/ui'
import { BodyText, CaptionText, CardTitle, EyebrowText, PageTitle } from '@/components/ui/Typography'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { BRAND_COLORS } from '@/lib/brandColors'
import { DASHBOARD_GRID_GAP } from '@/lib/dashboardToolbarUi'
import { CATALOG_STALE_TIME, queryKeys } from '@/lib/queryClient'
import {
  CONTENT_MAX_WIDTH,
  useIsMobile,
  useStableWindowWidth,
} from '@/lib/useDeviceType'
import {
  getAllInstructors,
  type InstructorListItem,
} from '@/supabase/services/instructors'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Image } from 'expo-image'
import { Link } from 'expo-router'
import React, { useCallback, useMemo, useState } from 'react'
import { Platform, RefreshControl } from 'react-native'
import { ScrollView, XStack, YStack } from 'tamagui'

const CONTAINER_PAD_X = 16
const GRID_GAP = DASHBOARD_GRID_GAP
const INSTRUCTOR_META_COLOR = BRAND_COLORS.instructorMeta

function columnsForWidth(inner: number): number {
  if (inner >= 1000) return 5
  if (inner >= 720) return 4
  if (inner >= 480) return 3
  return 2
}

export default function InstructorsScreen() {
  const isMobile = useIsMobile()
  const stableWidth = useStableWindowWidth()
  const [refreshing, setRefreshing] = useState(false)
  const queryClient = useQueryClient()
  useDocumentTitle('Instructors')

  const {
    data: instructors = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.instructors.all,
    queryFn: async () => {
      const rows = await getAllInstructors()
      for (const row of rows) {
        if (!row.slug) continue
        queryClient.setQueryData(queryKeys.instructors.bySlug(row.slug), row)
      }
      return rows
    },
    staleTime: CATALOG_STALE_TIME,
  })

  const cardWidth = useMemo(() => {
    const inner = Math.min(CONTENT_MAX_WIDTH, stableWidth) - CONTAINER_PAD_X * 2
    const cols = columnsForWidth(inner)
    return Math.floor((inner - GRID_GAP * (cols - 1)) / cols)
  }, [stableWidth])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    try {
      await refetch()
    } finally {
      setRefreshing(false)
    }
  }, [refetch])

  if (isLoading) {
    return (
      <YStack f={1} bg="$bg">
        <HelpScoutBeacon />
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
        >
          <CoursePagePremiumLoader variant="course" />
        </ScrollView>
      </YStack>
    )
  }

  if (error) {
    return (
      <YStack f={1} bg="$bg">
        <ErrorDisplay error={error as Error} onRetry={onRefresh} />
      </YStack>
    )
  }

  return (
    <YStack f={1} bg="$bg">
      <HelpScoutBeacon />
      <ScrollView
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={{
          flexGrow: 1,
          ...(Platform.OS === 'web' ? { minHeight: '100%' } : {}),
          paddingBottom: Platform.OS === 'web' ? 0 : 16,
        }}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <Container py={isMobile ? '$6' : '$8'} gap={isMobile ? '$6' : '$7'}>
          <YStack gap="$2" maw={640}>
            <PageTitle tag="h2" fontWeight="700" color="$color">
              Instructors
            </PageTitle>
            <CaptionText color={BRAND_COLORS.muted} size="$4">
              Meet the musicians who teach on Open Studio.
            </CaptionText>
            <EyebrowText color={BRAND_COLORS.instructorMeta}>
              {instructors.length}{' '}
              {instructors.length === 1 ? 'instructor' : 'instructors'}
            </EyebrowText>
          </YStack>

          {instructors.length === 0 ? (
            <YStack
              bg="#F4F1ED"
              borderRadius="$4"
              px="$4"
              py="$3"
              w="100%"
            >
              <BodyText color="$color" textAlign="center">
                No instructors available yet
              </BodyText>
            </YStack>
          ) : (
            <XStack
              flexWrap="wrap"
              columnGap={GRID_GAP}
              rowGap={isMobile ? 28 : 40}
              jc={isMobile ? 'center' : 'flex-start'}
            >
              {instructors.map((instructor) => (
                <InstructorCard
                  key={instructor.id}
                  instructor={instructor}
                  width={cardWidth}
                  useMobileAvatar={isMobile}
                />
              ))}
            </XStack>
          )}
        </Container>

        {Platform.OS === 'web' ? (
          <YStack flexGrow={1} minHeight={1} width="100%" />
        ) : null}
        <CoursePageFooter />
      </ScrollView>
    </YStack>
  )
}

type InstructorCardProps = {
  instructor: InstructorListItem
  width: number
  useMobileAvatar: boolean
}

const InstructorCard = React.memo(function InstructorCard({
  instructor,
  width,
  useMobileAvatar,
}: InstructorCardProps) {
  const avatarUri = useMobileAvatar
    ? instructor.avatar_url_mobile ?? instructor.avatar_url_desktop
    : instructor.avatar_url_desktop ?? instructor.avatar_url_mobile
  const initial = instructor.name?.trim().charAt(0).toUpperCase() || '?'

  return (
    <Link href={`/instructors/${instructor.slug}`} asChild>
    <YStack
      w={width}
      ai="center"
      gap="$3"
      style={{ cursor: Platform.OS === 'web' ? 'pointer' : undefined }}
      pressStyle={{ opacity: 0.9 }}
      accessibilityLabel={instructor.name}
    >
      {/* The portrait already ships with its own ring, so no extra border here —
          just clip to a circle and let it breathe with a subtle hover lift. */}
      <YStack
        w={width}
        aspectRatio={1}
        br={width}
        overflow="hidden"
        bg="#E8E4DF"
        ai="center"
        jc="center"
        animation="quick"
        hoverStyle={{ scale: 1.02 }}
      >
        {avatarUri ? (
          <Image
            source={{ uri: avatarUri }}
            style={{ width: '100%', height: '100%' }}
            contentFit="cover"
            recyclingKey={avatarUri}
          />
        ) : (
          <PageTitle color={BRAND_COLORS.muted} fontWeight="600">
            {initial}
          </PageTitle>
        )}
      </YStack>

      <YStack ai="center" gap="$1" w="100%" px="$2">
        <CardTitle
          color={BRAND_COLORS.text}
          fontWeight="600"
          textAlign="center"
          numberOfLines={2}
        >
          {instructor.name}
        </CardTitle>
        {instructor.instrument ? (
          <EyebrowText
            color={INSTRUCTOR_META_COLOR}
            textAlign="center"
            textTransform="capitalize"
            numberOfLines={1}
          >
            {instructor.instrument}
          </EyebrowText>
        ) : null}
      </YStack>
    </YStack>
    </Link>
  )
})
